import { ArrowLeft } from 'lucide-react';
import { Button, EmptyState, Skeleton } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { LoadFailed } from '../LoadFailed.tsx';
import { acceptsParcelChanges, carrierNameOf, useCarriers } from '../settings/carriers.ts';
import { CarrierDetailsForm } from './CarrierDetailsForm.tsx';
import { usePageContext } from '../usePageContext.ts';
import { EditOrderForm } from './EditOrderForm.tsx';
import type { Depot } from './OrderForm.tsx';
import { OrderFormFrame, type OrderFormData } from './OrderFormFrame.tsx';
import { isBeforeDispatch, isEditableAtCarrier, type Order } from './orders.ts';
import { useOrderDetail } from './useOrderDetail.ts';

export type EditOrderPageProps = {
  api: TypedClient;
  shipping: ShippingClient;
  orderId: string;
  onDone: (orderId: string) => void;
};

const depotOf = (order: Order, depots: readonly Depot[], archivedName: string): Depot =>
  depots.find((depot) => depot.id === order.locationId) ?? {
    id: order.locationId,
    name: archivedName,
    isDefault: false,
  };

export const EditOrderPage = ({ api, shipping, orderId, onDone }: EditOrderPageProps) => {
  const { t, describeError } = usePageContext();
  const { query, detail } = useOrderDetail(api, orderId);
  const carriers = useCarriers(shipping);
  const back = (
    <Button
      icon={<ArrowLeft size={20} aria-hidden="true" className="flip-inline" />}
      variant="outline"
      onClick={() => {
        onDone(orderId);
      }}
    >
      {t('editOrder.back')}
    </Button>
  );

  const body = (data: OrderFormData) => {
    if (query.isError)
      return (
        <LoadFailed
          description={describeError(query.error)}
          onRetry={() => {
            void query.refetch();
          }}
        />
      );
    if (detail === undefined || carriers.isPending)
      return <Skeleton height="240px" label={t('shell.loading')} />;
    const { order } = detail;
    const carrierCode = order.dispatch.carrierCode;
    if (
      order.piiVisible &&
      carrierCode !== null &&
      isEditableAtCarrier(order, acceptsParcelChanges(carriers.data?.items))
    )
      return (
        <CarrierDetailsForm
          api={api}
          order={order}
          carrierName={carrierNameOf(carriers.data?.items, carrierCode)}
          geography={data.geography}
          onDone={onDone}
        />
      );
    if (!isBeforeDispatch(order))
      return (
        <EmptyState
          title={t('editOrder.notEditable')}
          description={t('editOrder.notEditableHelp')}
          action={back}
        />
      );
    if (!order.piiVisible)
      return (
        <EmptyState title={t('editOrder.masked')} description={t('editOrder.maskedHelp')} action={back} />
      );
    return (
      <EditOrderForm
        api={api}
        order={order}
        currency={data.currency}
        geography={data.geography}
        depot={depotOf(order, data.depots, t('editOrder.archivedDepot'))}
        companies={data.companies}
        onDone={onDone}
      />
    );
  };

  return (
    <OrderFormFrame
      api={api}
      title={
        detail === undefined ? t('editOrder.title') : t('editOrder.titleOf', { number: detail.order.number })
      }
      context={t('editOrder.context')}
    >
      {body}
    </OrderFormFrame>
  );
};
